// createSimpleStoreFactory.js
import { message } from "ant-design-vue"
import { getAuth } from "firebase/auth"
import { defineStore } from "pinia"
import { FirebaseTokenLoader, JobsService } from "@telarya/telarya-sdk"

import { loadFromLocalStorage, saveToLocalStorage } from "@/components/Other/LocalStorageHelper"
import generateEndpoint from "@/endpoint"
import { createAuthenticatedHttpClient } from "@/services/httpClient"
import reportError from "@/utils/reportError"
import { selectedTenantId } from "@/init/authState"

export function createSimpleStoreFactory(
  storeObject,
  fetchFunction,
  objectClass,
  custom = {}, // <-- Additional store definitions (getters/actions/etc.)
  cacheDuration = 2 * 60 * 60 * 1000
) {
  const storeName = storeObject + "Store"

  return defineStore(storeName, {
    state: () => ({
      records: [],
      isFetching: false,
      fetchPromise: null,
      isInitialized: false,
      lastFetched: null,
      sdkService: null,
      socketService: null,
      tenantId: null,
      pusher: null,
      pusherChannelName: null
    }),
    getters: {
      /**
       * Default getters
       */
      get(state) {
        if (!this.isInitialized) {
          throw new Error(`${storeName}: Store is not initialized. Call 'initialize()' first.`)
        }
        return state.records.filter((r) => !r.isDeleted && !r.deletedAt)
      },
      getWithDeleted(state) {
        if (!this.isInitialized) {
          throw new Error(`${storeName}: Store is not initialized. Call 'initialize()' first.`)
        }
        return state.records
      },
      ...((custom.getters ?? {}) || {}) // Merge user-defined getters
    },
    actions: {
      setTenant(tenant) {
        if (!tenant || !tenant.id) {
          const error = new Error(`${storeName}: Invalid tenant provided.`)
          reportError(error, { context: "createSimpleStoreFactory.setTenant", store: storeName })
          throw error
        }
        this.tenantId = tenant.id
      },
      initializeSdkService() {
        if (this.sdkService) {
          return
        }
        this.sdkService = new JobsService(
          generateEndpoint(),
          createAuthenticatedHttpClient(new FirebaseTokenLoader(getAuth()), {
            tenantIdProvider: () => selectedTenantId.value
          })
        )
      },
      teardownPusher() {
        if (this.pusher && this.pusherChannelName) {
          try {
            this.pusher.unsubscribe(this.pusherChannelName)
          } catch (error) {
            reportError(error, {
              context: "createSimpleStoreFactory.teardownPusher",
              store: storeName,
              channel: this.pusherChannelName
            })
          }
        }
        this.pusherChannelName = null
      },
      resetStateForTenantSwitch() {
        this.teardownPusher()
        this.records = []
        this.isFetching = false
        this.fetchPromise = null
        this.lastFetched = null
        this.sdkService = null
        this.socketService = null
        this.pusher = null
        this.tenantId = null
        this.isInitialized = false
      },
      subscribeToPusher() {
        if (!this.tenantId) {
          const error = new Error(`${storeName}: Tenant ID is required for Pusher subscription.`)
          reportError(error, { context: "createSimpleStoreFactory.subscribeToPusher", store: storeName })
          throw error
        }
        if (!this.pusher) {
          const error = new Error(`${storeName}: No webSocket service.`)
          reportError(error, { context: "createSimpleStoreFactory.subscribeToPusher", store: storeName })
          throw error
        }

        const channelName = `${this.tenantId}_${storeObject}`
        this.pusherChannelName = channelName
        const channel = this.pusher.subscribe(channelName)

        // Some realtime payloads are entities, while the endpoint returns a
        // tenant-specific projection. Reload those instead of losing its metadata.
        if (custom.refreshOnRealtime) {
          const tenantId = this.tenantId
          const refresh = async () => {
            if (this.isFetching) await this.fetchPromise
            if (this.tenantId === tenantId) await this.fetch()
          }
          channel.bind("Create", refresh)
          channel.bind("Update", refresh)
          return
        }

        channel.bind("Create", (data) => {
          try {
            this.logToConsole(`Pusher message received on channel ${channelName}:`, "info")
            const instance = Object.create(objectClass.prototype)
            instance.init(lowercaseKeys(data))
            this.records.push(instance)
            this.logToConsole(`Added new ${storeObject} to records:`, "info")
            console.log(instance)
          } catch (error) {
            reportError(error, {
              context: "createSimpleStoreFactory.subscribeToPusher.create",
              store: storeName,
              channel: channelName
            })
          }
        })

        channel.bind("Update", (data) => {
          try {
            this.logToConsole(`Pusher message received on channel ${channelName}:`, "info")
            const instance = Object.create(objectClass.prototype)
            instance.init(lowercaseKeys(data))

            // Find or add
            const idx = this.records.findIndex((r) => r.id === instance.id)
            if (idx !== -1) {
              this.records[idx] = instance
              this.logToConsole(`Updated existing ${storeObject} in records:`, "info")
            } else {
              this.records.push(instance)
              this.logToConsole(`Added new ${storeObject} to records:`, "info")
            }
            console.log(instance)
          } catch (error) {
            reportError(error, {
              context: "createSimpleStoreFactory.subscribeToPusher.update",
              store: storeName,
              channel: channelName
            })
          }
        })

        this.logToConsole(`Subscribed to Pusher channel: ${channelName}`, "info")
      },
      getById(id) {
        if (!this.isInitialized) {
          const error = new Error(`${storeName}: Not initialized. Call 'initialize()' first.`)
          reportError(error, { context: "createSimpleStoreFactory.getById", store: storeName })
          throw error
        }
        if (id == null || id === "") return undefined
        return this.records.find((d) => d.id === id)
      },
      async fetch() {
        if (!this.tenantId) {
          this.logToConsole(`Tenant ID not set. Cannot fetch ${storeObject}.`, "error")
          return
        }
        if (this.isFetching) {
          return this.fetchPromise
        }

        this.isFetching = true
        this.fetchPromise = (async () => {
          try {
            this.logToConsole(`Fetching ${storeObject} for tenant ${this.tenantId}...`)
            this.initializeSdkService()
            const activeTenantId = this.tenantId
            const records = await fetchFunction(this.sdkService, activeTenantId)
            if (this.tenantId !== activeTenantId) {
              this.logToConsole(
                `Discarded ${storeObject} response for stale tenant ${activeTenantId}; current tenant ${this.tenantId}`,
                "info"
              )
              return
            }
            this.records = records
            this.lastFetched = Date.now()
            saveToLocalStorage(`${storeObject}-${activeTenantId}`, {
              records,
              timestamp: this.lastFetched
            })
            this.logToConsole("Updated cache/localStorage", "info")
          } catch (error) {
            reportError(error, { context: "createSimpleStoreFactory.fetch", store: storeName })
            this.notifyUser("Error fetching data. " + error.message, "error")
            console.error("Failed to fetch:", error)
          } finally {
            this.isFetching = false
            this.fetchPromise = null
          }
        })()
        return this.fetchPromise
      },
      initialize(tenant, webSocket) {
        if (!tenant || !tenant.id) {
          const error = new Error(`${storeName}: Invalid tenant provided during init.`)
          reportError(error, { context: "createSimpleStoreFactory.initialize", store: storeName })
          throw error
        }
        if (!webSocket) {
          const error = new Error(`${storeName}: Invalid webSocket provided during init.`)
          reportError(error, { context: "createSimpleStoreFactory.initialize", store: storeName })
          throw error
        }

        const tenantChanged = this.tenantId && this.tenantId !== tenant.id
        if (tenantChanged) {
          this.resetStateForTenantSwitch()
        } else if (this.isInitialized) {
          return
        }

        this.setTenant(tenant)
        this.pusher = webSocket
        this.initializeSdkService()

        const cached = loadFromLocalStorage(`${storeObject}-${this.tenantId}`)
        if (cached) {
          const { records, timestamp } = cached
          const isCacheValid = Date.now() - timestamp < cacheDuration
          if (isCacheValid) {
            this.records = records
            this.lastFetched = timestamp
            this.logToConsole(`Loaded valid ${storeObject} for tenant ${this.tenantId} from localStorage`, "info")
          } else {
            this.logToConsole(`Cached ${storeObject} data for tenant ${this.tenantId} is stale`, "info")
          }
        }

        this.fetch()
          .then(() => this.logToConsole(`Fetched ${storeObject} for tenant ${this.tenantId}`, "info"))
          .catch((error) => {
            reportError(error, { context: "createSimpleStoreFactory.initialize", store: storeName })
            console.error("Error fetching:", error)
          })

        this.subscribeToPusher()
        this.isInitialized = true
      },
      notifyUser(msg, type = "info") {
        message.error(storeName + " " + `[${type.toUpperCase()}] ${msg}`)
      },
      logToConsole(msg, type = "info") {
        console.log(storeName + " " + `[${type.toUpperCase()}] ${msg}`)
      },
      ...(custom.actions ?? {}) // Merge user-defined actions
    }
  })
}

function lowercaseKeys(obj) {
  if (Array.isArray(obj)) {
    return obj.map(lowercaseKeys)
  } else if (obj !== null && typeof obj === "object") {
    return Object.fromEntries(
      Object.entries(obj).map(([key, value]) => [key.charAt(0).toLowerCase() + key.slice(1), lowercaseKeys(value)])
    )
  } else {
    return obj
  }
}
